import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Info, AlertTriangle, ShieldAlert, CheckCircle2 } from 'lucide-react';

// GetCare 360 — App Dialog Design System.
// Replaces native window.alert / confirm / prompt with a consistent, branded UI.
// Built on shadcn Dialog (focus-trap, ESC-to-close, aria already wired).

function getVariantParts(variant) {
  if (variant === 'warning') return { Icon: AlertTriangle, iconCls: 'text-amber-700', ringCls: 'bg-amber-100' };
  if (variant === 'danger')  return { Icon: ShieldAlert,   iconCls: 'text-red-700',   ringCls: 'bg-red-100' };
  if (variant === 'success') return { Icon: CheckCircle2,  iconCls: 'text-emerald-700', ringCls: 'bg-emerald-100' };
  return { Icon: Info, iconCls: 'text-slate-600', ringCls: 'bg-slate-100' };
}

function getInfoNoteCls(variant) {
  if (variant === 'warning') return 'bg-amber-50 border-amber-200 text-amber-900';
  if (variant === 'danger')  return 'bg-red-50 border-red-200 text-red-900';
  if (variant === 'success') return 'bg-emerald-50 border-emerald-200 text-emerald-900';
  return 'bg-blue-50 border-blue-200 text-blue-900';
}


export function AppAlertModal({
  open, onOpenChange, title, description,
  variant = 'info', okLabel = 'OK', onOk, children,
  testId = 'app-alert-modal',
}) {
  const parts = getVariantParts(variant);
  const Icon = parts.Icon;
  const handleOk = () => { if (onOk) onOk(); if (onOpenChange) onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] rounded-2xl shadow-xl" data-testid={testId}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className={'shrink-0 rounded-full p-2 ' + parts.ringCls}>
              <Icon className={'w-5 h-5 ' + parts.iconCls} aria-hidden="true" />
            </div>
            <div className="space-y-1 pt-0.5">
              <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
              {description && <DialogDescription className="text-sm">{description}</DialogDescription>}
            </div>
          </div>
        </DialogHeader>
        {children && <div className="text-sm space-y-2 pt-1">{children}</div>}
        <DialogFooter className="pt-3">
          <Button onClick={handleOk} data-testid={testId + '-ok'}>{okLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function AppConfirmModal({
  open, onOpenChange, title, description,
  variant = 'info',
  cancelLabel = 'Cancel', confirmLabel = 'Confirm',
  onConfirm, busy = false,
  beforeAfter, infoNote, children,
  testId = 'app-confirm-modal',
}) {
  const parts = getVariantParts(variant);
  const Icon = parts.Icon;
  const baLabel = beforeAfter ? beforeAfter.label : null;
  const baBefore = beforeAfter ? beforeAfter.before : null;
  const baAfter = beforeAfter ? beforeAfter.after : null;
  const handleCancel = () => { if (onOpenChange) onOpenChange(false); };
  const handleConfirm = () => { if (onConfirm) onConfirm(); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] rounded-2xl shadow-xl" data-testid={testId}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className={'shrink-0 rounded-full p-2 ' + parts.ringCls}>
              <Icon className={'w-5 h-5 ' + parts.iconCls} aria-hidden="true" />
            </div>
            <div className="space-y-1 pt-0.5">
              <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
              {description && <DialogDescription className="text-sm">{description}</DialogDescription>}
            </div>
          </div>
        </DialogHeader>
        <div className="text-sm space-y-2 pt-1">
          {beforeAfter && (
            <div className="rounded-md border bg-muted/30 p-2.5 text-xs space-y-1">
              {baLabel && <div className="text-muted-foreground">{baLabel}</div>}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Current</div>
                  <div className="font-medium tabular-nums">{baBefore}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">After</div>
                  <div className="font-semibold tabular-nums text-emerald-700">{baAfter}</div>
                </div>
              </div>
            </div>
          )}
          {infoNote && (
            <div className={'rounded-md border px-2.5 py-2 text-[11px] leading-relaxed ' + getInfoNoteCls(variant)}>
              {infoNote}
            </div>
          )}
          {children}
        </div>
        <DialogFooter className="pt-3 gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleCancel}
                  disabled={busy} data-testid={testId + '-cancel'}>{cancelLabel}</Button>
          <Button onClick={handleConfirm}
                  disabled={busy} data-testid={testId + '-confirm'}>{busy ? 'Working…' : confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function AppDangerConfirmModal({
  open, onOpenChange, title, description,
  cancelLabel = 'Cancel', confirmLabel = 'Delete',
  onConfirm, busy = false,
  requireTypeToConfirm, reasonRequired = false, children,
  testId = 'app-danger-confirm-modal',
}) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => { if (!open) { setTyped(''); setReason(''); } }, [open]);
  const typeOk = !requireTypeToConfirm || typed.trim() === requireTypeToConfirm;
  const reasonOk = !reasonRequired || reason.trim().length >= 3;
  const canConfirm = typeOk && reasonOk && !busy;
  const handleCancel = () => { if (onOpenChange) onOpenChange(false); };
  const handleConfirm = () => { if (onConfirm) onConfirm({ reason: reason }); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] rounded-2xl shadow-xl" data-testid={testId}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-full p-2 bg-red-100">
              <ShieldAlert className="w-5 h-5 text-red-700" aria-hidden="true" />
            </div>
            <div className="space-y-1 pt-0.5">
              <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
              {description && <DialogDescription className="text-sm">{description}</DialogDescription>}
            </div>
          </div>
        </DialogHeader>
        <div className="text-sm space-y-2 pt-1">
          {children}
          {reasonRequired && (
            <div className="space-y-1">
              <label className="text-[11px] text-muted-foreground">Reason (required, min 3 chars)</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                        className="w-full text-sm rounded-md border bg-background px-2.5 py-1.5"
                        rows={2} data-testid={testId + '-reason'} />
            </div>
          )}
          {requireTypeToConfirm && (
            <div className="space-y-1 pt-1">
              <label className="text-[11px] text-muted-foreground">Type <code className="text-[11px] font-semibold">{requireTypeToConfirm}</code> to confirm</label>
              <input value={typed} onChange={(e) => setTyped(e.target.value)}
                     className="w-full text-sm rounded-md border bg-background px-2.5 py-1.5 font-mono"
                     data-testid={testId + '-type-to-confirm'} />
            </div>
          )}
        </div>
        <DialogFooter className="pt-3 gap-2 sm:gap-2">
          <Button variant="outline" onClick={handleCancel}
                  disabled={busy} data-testid={testId + '-cancel'}>{cancelLabel}</Button>
          <Button variant="destructive"
                  onClick={handleConfirm}
                  disabled={!canConfirm}
                  data-testid={testId + '-confirm'}>{busy ? 'Working…' : confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export function AppFormModal({
  open, onOpenChange, title, description,
  variant = 'info',
  cancelLabel = 'Cancel', submitLabel = 'Save',
  onSubmit, busy = false, children,
  testId = 'app-form-modal',
}) {
  const parts = getVariantParts(variant);
  const Icon = parts.Icon;
  const handleCancel = () => { if (onOpenChange) onOpenChange(false); };
  const handleSubmit = (e) => { e.preventDefault(); if (onSubmit) onSubmit(e); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] rounded-2xl shadow-xl" data-testid={testId}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className={'shrink-0 rounded-full p-2 ' + parts.ringCls}>
              <Icon className={'w-5 h-5 ' + parts.iconCls} aria-hidden="true" />
            </div>
            <div className="space-y-1 pt-0.5">
              <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
              {description && <DialogDescription className="text-sm">{description}</DialogDescription>}
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 pt-1">
          {children}
          <DialogFooter className="pt-2 gap-2 sm:gap-2">
            <Button type="button" variant="outline"
                    onClick={handleCancel}
                    disabled={busy} data-testid={testId + '-cancel'}>{cancelLabel}</Button>
            <Button type="submit" disabled={busy} data-testid={testId + '-submit'}>
              {busy ? 'Saving…' : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
